import type { PayloadAction, ReducerCreators } from '@reduxjs/toolkit';

import { createAppSlice } from '../create-app-slice';
import { customerFeatureStateSlice } from '@features/customer';
import { State } from './state.type';
import { stateInitializer } from './state-initializer';
import { ViewMode } from '@features/view/view-mode.enum';

// If you are not using async thunks you can use the standalone `createSlice`.
export const slice = createAppSlice({
	name: 'feature',
	// `createSlice` will infer the state type from the `initialState` argument
	initialState: stateInitializer(),
	// The `reducers` field lets us define reducers and generate associated actions
	reducers: (create) => ({
		setViewMode: create.reducer((state, action: PayloadAction<ViewMode>) => {
			state.viewMode.viewMode = action.payload;
		}),
		setAccessoryDropDown: create.reducer((state, action: PayloadAction<boolean>) => {
			state.accessories.showAccessoryDropdown = action.payload;
		}),
		setSelectedAccessory: create.reducer((state, action: PayloadAction<string[]>) => {
			state.accessories.selectedAccessory = action.payload;
		}),
		setRemovedAccessory: create.reducer((state, action: PayloadAction<string>) => {
			state.accessories.removedAccessory = action.payload;
		}),
		setPromotionalSkuDropdown: create.reducer((state, action: PayloadAction<boolean>) => {
			state.promotionalSku.showPromotionalDropdown = action.payload;
		}),
		setSelectedPromotionalSku: create.reducer((state, action: PayloadAction<string[]>) => {
			state.promotionalSku.selectedPromotionalSku = action.payload;
		}),
		setRemovedPromotionalSku: create.reducer((state, action: PayloadAction<string>) => {
			state.promotionalSku.removedPromotionalSku = action.payload;
		}),
		setPricePromotionalSku: create.reducer((state, action: PayloadAction<any[]>) => {
			state.promotionalSku.pricePromotionalSku = action.payload;
		}),
		setPromotionalDescription: create.reducer((state, action: PayloadAction<string[]>) => {
			state.promotionalSku.promotionalDescription = action.payload;
		}),
		setSkuPromotionalSku: create.reducer((state, action: PayloadAction<string[]>) => {
			state.promotionalSku.skuPromotionalSku = action.payload;
		}),
		setCommentAdded: create.reducer((state, action: PayloadAction<string[]>) => {
			state.comments.commentAdded = action.payload;
		}),
		...(customerFeatureStateSlice.reducers<State>(create))
	}),
	// You can define your selectors here. These selectors receive the slice
	// state as their first argument.
	selectors: {
		...customerFeatureStateSlice.selectors,
		selectFeatureState: (state) => state,
		selectFeatureLayerState: (state) => state.layer,
		selectFeatureValidationState: (state) => state.validation,
		selectGetNewCustomerData: (state) => state.customer.modals.getNewCustomerData,
		selectViewMode: (state) => state.viewMode.viewMode,
		selectAccessoryDropdown: (state) => state.accessories.showAccessoryDropdown,
		selectSelectedAccessory: (state) => state.accessories.selectedAccessory,
		selectRemovedAccessory: (state) => state.accessories.removedAccessory,
		selectPromotionalSkuDropdown: (state) => state.promotionalSku.showPromotionalDropdown,
		selectSelectedPromotionalSku: (state) => state.promotionalSku.selectedPromotionalSku,
		selectRemovedPromotionalSku: (state) => state.promotionalSku.removedPromotionalSku,
		selectPriceChangePromotionalSku: (state) => state.promotionalSku.pricePromotionalSku,
		selectPromotionalDescription: (state) => state.promotionalSku.promotionalDescription,
		selectSkuPromotionalSku: (state) => state.promotionalSku.skuPromotionalSku,
		selectCommentAdded: (state) => state.comments.commentAdded
	}
});
